import { useRef } from 'react'
import { useFocusTrap } from '@/lib/useFocusTrap'
import { cn } from '@/lib/utils'

/**
 * A bottom sheet — one of the two things in the app that lifts (ADR-0025 §3).
 *
 * ── Hand-rolled rather than a dependency ──
 *
 * The alternative (`@radix-ui/react-dialog`) is a new runtime dependency plus its focus-management
 * peer, for one component in one place. The four things a dialog has to get right are small enough
 * to own and all testable: Escape closes, focus is trapped, focus returns where it came from, and
 * the page behind does not scroll. All four live in `lib/useFocusTrap.ts`, shared with
 * `PhotoViewer`; what is left here is the sheet's shape and motion.
 *
 * ── Native `<dialog>` was tried and rejected ──
 *
 * `showModal()` gives Escape, focus trapping and the top layer for free, but its `::backdrop` cannot
 * be animated in step with the sheet in Safari, and a `<dialog>` sized to the bottom of the viewport
 * fights `env(safe-area-inset-bottom)` because the top layer is not in the normal flow. The
 * behaviours are cheaper to write than those two bugs are to work around.
 */
export function Sheet({
  open,
  onClose,
  labelledBy,
  children,
}: {
  open: boolean
  onClose: () => void
  /** The `id` of the sheet's heading — its accessible name. */
  labelledBy?: string
  children: React.ReactNode
}) {
  const panelRef = useRef<HTMLDivElement>(null)

  // Escape, the focus trap, focus restoration and the body scroll lock — all four, and `open` is what
  // stands them down. The hook must be called before the `open` early return below, not after.
  useFocusTrap(panelRef, onClose, open)

  if (!open) return null

  return (
    /**
     * The backdrop closes the sheet. `onMouseDown` rather than `onClick`: with `onClick`, a drag that
     * *starts* inside the sheet and *ends* on the backdrop counts as a backdrop click and discards
     * what the user was typing.
     *
     * `noStaticElementInteractions` is suppressed rather than satisfied, because satisfying it would
     * mean giving the backdrop a `button` role — which would put a second, unlabelled "close" control
     * in the tab order *inside* the focus trap, immediately before the real one. The backdrop is
     * decoration for pointer users; the keyboard paths to close are Escape (tested) and the sheet's
     * own Cancel button, and both are real controls.
     */
    // biome-ignore lint/a11y/noStaticElementInteractions: a dialog backdrop is pointer-only decoration; Escape and Cancel are the accessible close paths
    <div
      className="fixed inset-0 z-50 flex items-end bg-[rgb(10_10_9_/_0.42)] [animation:ledger-fade_var(--m-fast)_ease-out]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      {/* `tabIndex={-1}` makes the panel programmatically focusable — it is the fallback focus target
          when the sheet holds no field at all. It stays out of the tab order. */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={cn(
          'max-h-[92%] w-full overflow-y-auto overscroll-contain bg-paper shadow-sheet',
          // 22px top corners only: the sheet is anchored to the bottom edge, so rounding the bottom
          // would float it and reveal the backdrop beneath the home indicator.
          'rounded-t-4',
          'px-gutter pt-2 pb-[max(env(safe-area-inset-bottom),1.875rem)]',
          '[animation:ledger-rise_var(--m-base)_var(--m-ease)]',
        )}
      >
        {/* The grab handle. Decoration — this sheet is not draggable, and the handle is there
            because its absence makes a bottom sheet read as a stuck panel. `aria-hidden` so it is
            not announced as content. */}
        <div aria-hidden="true" className="flex justify-center pt-1.5 pb-3.5">
          <div className="h-1 w-10 rounded-[2px] bg-rule-2" />
        </div>
        {children}
      </div>
    </div>
  )
}
